import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  ApplyTransformationRuleRequest,
  CanvasGraphModel,
  CreateManualAdjustmentRequest,
  FieldMapping,
  MappingResponse,
  TransformationRule,
  TransformationStep,
  UpdateMappingRequest,
  apiService,
} from '../../services/api';
import type { CanvasGraph, CanvasSelection } from '../types';
import type { TransformationFormValues } from '../InspectorPanel';
import type { GuardrailStatus } from './useGuardrailStatus';
import { evaluateFieldTypeCompatibility, type TypeCompatibilityIssue } from '../validation/typeCompatibility';

type StatusMessage = { type: 'success' | 'error' | 'warning'; message: string };

const toErrorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback;
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null && 'message' in error && typeof (error as Record<string, unknown>).message === 'string') return (error as Record<string, unknown>).message as string;
  return fallback;
};

const parseDefaultValue = (raw: string): unknown => {
  const trimmed = raw.trim();
  if (!trimmed.length) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
};

const stringifyDefaultValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

export interface UseCanvasMutationsOptions {
  batchId?: string;
  activeWorkbookId?: string;
  mappingResponse?: MappingResponse;
  canvasGraph: CanvasGraph;
  guardrailStatus: GuardrailStatus;
  selectedProjectId?: string | null;
  schemaId?: string | null;
  schemaSetName?: string;
  generationMode?: 'auto' | 'full';
  onToast: (msg: StatusMessage) => void;
  onInspectorStatus: (msg: StatusMessage | null) => void;
  onInspectorError: (msg: string | null) => void;
  recordCanvasEvent: (kind: string, payload: Record<string, string>) => void;
  clearSelection: () => void;
  setPendingCreateSelection: (val: { targetTable: string; targetField: string } | null) => void;
  refetchCanvasGraph: () => Promise<unknown>;
}

export function useCanvasMutations({
  batchId,
  activeWorkbookId,
  mappingResponse,
  canvasGraph,
  guardrailStatus,
  selectedProjectId,
  schemaId,
  generationMode,
  onToast,
  onInspectorStatus,
  onInspectorError,
  recordCanvasEvent,
  clearSelection,
  setPendingCreateSelection,
  refetchCanvasGraph,
}: UseCanvasMutationsOptions) {
  const queryClient = useQueryClient();
  const pendingManualAdjustmentRef = useRef<CreateManualAdjustmentRequest | null>(null);
  const [undoingAdjustmentId, setUndoingAdjustmentId] = useState<string | null>(null);
  const [redoingAdjustmentId, setRedoingAdjustmentId] = useState<string | null>(null);
  const [isRegeneratingMapping, setIsRegeneratingMapping] = useState(false);
  const [isValidatingMapping, setIsValidatingMapping] = useState(false);
  const [isCreatingMapping, setIsCreatingMapping] = useState(false);
  const [deleteMappingDialog, setDeleteMappingDialog] = useState<{
    open: boolean;
    mapping: FieldMapping | null;
  }>({ open: false, mapping: null });

  // Phase 4.7: Type mismatch popover state for drag-to-connect
  const [typeMismatch, setTypeMismatch] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    sourceType: string;
    targetType: string;
    sourceField: string;
    targetField: string;
    sourceTable: string;
    targetTable: string;
    issues: TypeCompatibilityIssue[];
  } | null>(null);

  // --- Canvas graph cache helper ---
  const updateCanvasGraphCache = useCallback(
    (graph: CanvasGraphModel | undefined | null) => {
      if (!graph || !activeWorkbookId) return;
      queryClient.setQueryData<CanvasGraphModel>(['canvas-graph', activeWorkbookId], graph);
    },
    [activeWorkbookId, queryClient],
  );

  const flushManualAdjustment = useCallback(() => {
    const draft = pendingManualAdjustmentRef.current;
    if (!draft || !activeWorkbookId) {
      pendingManualAdjustmentRef.current = null;
      return;
    }
    pendingManualAdjustmentRef.current = null;
    apiService
      .createCanvasManualAdjustment(activeWorkbookId, draft)
      .then((cg) => {
        updateCanvasGraphCache(cg);
        void refetchCanvasGraph();
      })
      .catch((error) => {
        onToast({
          type: 'error',
          message: toErrorMessage(error, 'Failed to record manual adjustment.'),
        });
      });
  }, [activeWorkbookId, updateCanvasGraphCache, refetchCanvasGraph, onToast]);

  // --- Generate ---
  const generateMappingsMutation = useMutation(
    async () => {
      if (!batchId) throw new Error('Missing batch identifier.');
      if (!selectedProjectId)
        throw new Error('Select a project before regenerating mappings.');
      return apiService.generateMappings(batchId, {
        projectId: selectedProjectId,
        targetSchemaId: schemaId ?? null,
        generationMode,
      });
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['mappings', batchId], data);
        onToast({ type: 'success', message: 'Canvas regenerated with latest draft.' });
        recordCanvasEvent('canvas.mapping.regenerated', {
          batch_id: batchId ?? '',
          recipe_id: data.recipe_id ?? '',
        });
      },
      onError: (mutationError) => {
        onToast({
          type: 'error',
          message: toErrorMessage(mutationError, 'Unable to regenerate mappings.'),
        });
      },
    },
  );

  // --- Approve ---
  const approveMappingsMutation = useMutation(
    () =>
      apiService.approveMappings(batchId!, mappingResponse?.recipe_id ?? '', 'web-ui'),
    {
      onSuccess: async () => {
        onToast({ type: 'success', message: 'Mapping approved successfully.' });
        await queryClient.refetchQueries(['mappings', batchId]);
        if (selectedProjectId) {
          await queryClient.invalidateQueries(['project-summary', selectedProjectId]);
        }
        recordCanvasEvent('canvas.mapping.approved', {
          batch_id: batchId ?? '',
          recipe_id: mappingResponse?.recipe_id ?? '',
        });
      },
      onError: (mutationError) => {
        onToast({
          type: 'error',
          message: toErrorMessage(mutationError, 'Unable to approve mapping.'),
        });
      },
    },
  );

  // --- Update mapping ---
  const updateMappingMutation = useMutation(
    ({
      mappingId,
      payload,
      targetField,
    }: {
      mappingId: string;
      payload: UpdateMappingRequest;
      targetField?: string;
    }) => apiService.updateMapping(batchId!, mappingId, payload),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(['mappings', batchId], data);

        // Check for validation warnings in the save response
        const warnings = data?.warnings;
        if (warnings && Array.isArray(warnings) && warnings.length > 0) {
          const warningMsgs = warnings.slice(0, 2).map((w) => w.message || w.rule_type).join('; ');
          const suffix = warnings.length > 2 ? ` (+${warnings.length - 2} more)` : '';
          onInspectorStatus({ type: 'warning', message: `Saved with warnings: ${warningMsgs}${suffix}` });
          const savedField = (variables.targetField ?? '').trim().toLowerCase();
          const fieldWarnings = savedField
            ? warnings.filter(
                (w) => (w.target_field ?? '').trim().toLowerCase() === savedField,
              )
            : [];
          if (fieldWarnings.length > 0) {
            const toastMsgs = fieldWarnings
              .slice(0, 2)
              .map((w) => w.message || w.rule_type)
              .join('; ');
            const toastSuffix =
              fieldWarnings.length > 2 ? ` (+${fieldWarnings.length - 2} more)` : '';
            onToast({
              type: 'warning',
              message: `Saved with warnings: ${toastMsgs}${toastSuffix}`,
            });
          } else {
            onToast({ type: 'success', message: 'Transformation saved.' });
          }
        } else {
          onInspectorStatus({ type: 'success', message: 'Transformation saved.' });
          onToast({ type: 'success', message: 'Transformation saved.' });
        }
        onInspectorError(null);
        flushManualAdjustment();
        if (mappingResponse?.recipe_id) {
          recordCanvasEvent('canvas.mapping.updated', {
            batch_id: batchId ?? '',
            recipe_id: mappingResponse.recipe_id,
            mapping_id: variables?.mappingId ?? '',
          });
        }
      },
      onError: (mutationError) => {
        const message = toErrorMessage(mutationError, 'Unable to save transformation.');
        onInspectorError(message);
        onInspectorStatus({ type: 'error', message });
        onToast({ type: 'error', message });
      },
      onSettled: () => {
        queryClient.invalidateQueries(['mappings', batchId]);
        queryClient.invalidateQueries(['mapping-health', batchId]);
      },
    },
  );

  // --- Delete mapping ---
  const deleteMappingMutation = useMutation(
    (mappingId: string) =>
      apiService.deleteMapping(batchId!, mappingId, mappingResponse!.recipe_id!),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['mappings', batchId], data);
        onToast({ type: 'success', message: 'Mapping deleted.' });
        clearSelection();
        setDeleteMappingDialog({ open: false, mapping: null });
        recordCanvasEvent('canvas.mapping.deleted', {
          batch_id: batchId ?? '',
          recipe_id: mappingResponse?.recipe_id ?? '',
        });
      },
      onError: (mutationError) => {
        onToast({
          type: 'error',
          message: toErrorMessage(mutationError, 'Unable to delete mapping.'),
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries(['mappings', batchId]);
      },
    },
  );

  const handleDeleteMapping = useCallback((mapping: FieldMapping) => {
    setDeleteMappingDialog({ open: true, mapping });
  }, []);

  const handleConfirmDeleteMapping = useCallback(() => {
    if (deleteMappingDialog.mapping) {
      deleteMappingMutation.mutate(deleteMappingDialog.mapping.mapping_id);
    }
  }, [deleteMappingDialog.mapping, deleteMappingMutation]);

  // --- Apply rule ---
  const applyRuleMutation = useMutation(
    (payload: ApplyTransformationRuleRequest) =>
      apiService.applyTransformationRule(batchId!, payload),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(['mappings', batchId], data);
        onInspectorStatus({ type: 'success', message: 'Rule applied to mapping.' });
        onInspectorError(null);
        onToast({ type: 'success', message: 'Rule applied to mapping.' });
        flushManualAdjustment();
        recordCanvasEvent('canvas.rule.applied', {
          batch_id: batchId ?? '',
          recipe_id: mappingResponse?.recipe_id ?? '',
          mapping_id: variables?.mapping_id ?? '',
          rule_id: variables?.rule_id ?? '',
        });
      },
      onError: (mutationError) => {
        const message = toErrorMessage(mutationError, 'Unable to apply rule.');
        onInspectorStatus({ type: 'error', message });
        onInspectorError(message);
        onToast({ type: 'error', message });
      },
      onSettled: () => {
        queryClient.invalidateQueries(['mappings', batchId]);
      },
    },
  );

  // --- Undo / Redo ---
  const undoManualAdjustmentMutation = useMutation(
    async (adjustmentId: string) => {
      if (!activeWorkbookId) throw new Error('Canvas workbook unavailable.');
      return apiService.undoCanvasManualAdjustment(activeWorkbookId, adjustmentId, {
        actor: 'web-ui',
      });
    },
    {
      onSuccess: (cg) => {
        onToast({ type: 'success', message: 'Manual adjustment undone.' });
        updateCanvasGraphCache(cg);
        void refetchCanvasGraph();
      },
      onError: (mutationError) => {
        onToast({
          type: 'error',
          message: toErrorMessage(mutationError, 'Unable to undo manual adjustment.'),
        });
      },
    },
  );

  const redoManualAdjustmentMutation = useMutation(
    async (adjustmentId: string) => {
      if (!activeWorkbookId) throw new Error('Canvas workbook unavailable.');
      return apiService.redoCanvasManualAdjustment(activeWorkbookId, adjustmentId, {
        actor: 'web-ui',
      });
    },
    {
      onSuccess: (cg) => {
        onToast({ type: 'success', message: 'Manual adjustment redone.' });
        updateCanvasGraphCache(cg);
        void refetchCanvasGraph();
      },
      onError: (mutationError) => {
        onToast({
          type: 'error',
          message: toErrorMessage(mutationError, 'Unable to redo manual adjustment.'),
        });
      },
    },
  );

  const handleUndoAdjustment = useCallback(
    async (adjustmentId: string) => {
      if (!activeWorkbookId) {
        onToast({ type: 'error', message: 'Canvas workbook unavailable.' });
        return;
      }
      setUndoingAdjustmentId(adjustmentId);
      try {
        await undoManualAdjustmentMutation.mutateAsync(adjustmentId);
      } finally {
        setUndoingAdjustmentId(null);
      }
    },
    [activeWorkbookId, undoManualAdjustmentMutation, onToast],
  );

  const handleRedoAdjustment = useCallback(
    async (adjustmentId: string) => {
      if (!activeWorkbookId) {
        onToast({ type: 'error', message: 'Canvas workbook unavailable.' });
        return;
      }
      setRedoingAdjustmentId(adjustmentId);
      try {
        await redoManualAdjustmentMutation.mutateAsync(adjustmentId);
      } finally {
        setRedoingAdjustmentId(null);
      }
    },
    [activeWorkbookId, redoManualAdjustmentMutation, onToast],
  );

  // --- Create mapping ---
  const handleCreateMapping = useCallback(
    async (
      targetTable: string,
      targetField: string,
      sourceFile?: string,
      sourceField?: string,
      transformation?: string,
      transformationType?: string,
      operation?: string,
      parameters?: Record<string, unknown>,
    ) => {
      if (!activeWorkbookId) {
        onToast({ type: 'error', message: 'No active workbook found' });
        return;
      }
      setIsCreatingMapping(true);
      try {
        const hasSource = Boolean(sourceFile?.trim()) && Boolean(sourceField?.trim());
        const hasTransformation = Boolean(transformation?.trim());
        const payload = {
          target_table: targetTable,
          target_field: targetField,
          source_file: hasSource ? sourceFile : undefined,
          source_field: hasSource ? sourceField : undefined,
          transformation: hasTransformation
            ? transformation
            : hasSource ? `${sourceFile}.${sourceField}` : '',
          transformation_type: transformationType
            ?? (hasSource ? 'direct' : 'expression'),
          confidence: hasSource || hasTransformation ? 0.5 : 0.0,
          ...(operation ? { operation } : {}),
          ...(parameters ? { parameters } : {}),
        };
        await apiService.createMapping(activeWorkbookId, payload);
        onToast({ type: 'success', message: 'Mapping created successfully' });
        clearSelection();
        await queryClient.invalidateQueries(['mappings', batchId]);
        await refetchCanvasGraph();
        setPendingCreateSelection({ targetTable, targetField });
      } catch (err) {
        console.error('Failed to create mapping:', err);
        const msg = err instanceof Error ? err.message : 'Failed to create mapping';
        onToast({ type: 'error', message: msg });
      } finally {
        setIsCreatingMapping(false);
      }
    },
    [activeWorkbookId, queryClient, refetchCanvasGraph, onToast, clearSelection, setPendingCreateSelection],
  );

  // --- Drag-to-connect ---
  const handleCanvasConnect = useCallback(
    async (connection: { source: string | null; target: string | null }, event?: React.MouseEvent) => {
      if (!connection.source || !connection.target || !activeWorkbookId) return;

      const sourceNode = canvasGraph.nodes.find((n) => n.id === connection.source);
      const targetNode = canvasGraph.nodes.find((n) => n.id === connection.target);
      if (!sourceNode || !targetNode) return;

      if (
        sourceNode.data.kind !== 'sourceField' ||
        targetNode.data.kind !== 'targetField'
      ) {
        onToast({
          type: 'error',
          message: 'Drag from a source field to a target field to create a mapping.',
        });
        return;
      }

      const sourceTable = sourceNode.data.table;
      const sourceField = sourceNode.data.field;
      const targetTable = targetNode.data.table;
      const targetField = targetNode.data.field;

      if (!targetTable || !targetField) {
        onToast({ type: 'error', message: 'Invalid target field.' });
        return;
      }

      // Enhanced type compatibility check via evaluateFieldTypeCompatibility
      const sourceType = (sourceNode.data.metadata?.data_type as string) || '';
      const targetType = (targetNode.data.metadata?.data_type as string) || '';
      const sourceNullable = sourceNode.data.metadata?.nullable !== false;
      const targetNullable = targetNode.data.metadata?.is_nullable !== false;

      const typeIssues = (sourceType && targetType)
        ? evaluateFieldTypeCompatibility({
            sourceField,
            sourceType,
            targetField,
            targetType,
            sourceNullable,
            targetNullable,
          })
        : [];

      if (typeIssues.length > 0 && event) {
        // Show type mismatch popover with detailed issues — defer creation until user decides
        setTypeMismatch({
          open: true,
          anchorEl: event.currentTarget as HTMLElement,
          sourceType,
          targetType,
          sourceField,
          targetField,
          sourceTable,
          targetTable,
          issues: typeIssues,
        });
        return;
      }

      setIsCreatingMapping(true);
      try {
        await apiService.createMapping(activeWorkbookId, {
          target_table: targetTable,
          target_field: targetField,
          source_file: sourceTable || undefined,
          source_field: sourceField || undefined,
          transformation: sourceField ? `${sourceTable}.${sourceField}` : '',
          transformation_type: 'direct',
        });
        onToast({ type: 'success', message: `Mapped ${sourceField} → ${targetField}` });
        clearSelection();
        await queryClient.invalidateQueries(['mappings', batchId]);
        await refetchCanvasGraph();
        setPendingCreateSelection({ targetTable, targetField });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to create mapping';
        onToast({ type: 'error', message: msg });
      } finally {
        setIsCreatingMapping(false);
      }
    },
    [activeWorkbookId, canvasGraph.nodes, queryClient, refetchCanvasGraph, onToast, clearSelection, setPendingCreateSelection],
  );

  // Phase 4.7: Create mapping with CAST when type mismatch is resolved
  const handleTypeMismatchApplyCast = useCallback(async () => {
    if (!typeMismatch || !activeWorkbookId) return;
    const { sourceTable, sourceField, targetTable, targetField, targetType } = typeMismatch;
    setTypeMismatch(null);
    setIsCreatingMapping(true);
    try {
      await apiService.createMapping(activeWorkbookId, {
        target_table: targetTable,
        target_field: targetField,
        source_file: sourceTable || undefined,
        source_field: sourceField || undefined,
        transformation: `CAST("${sourceField}" AS ${targetType})`,
        transformation_type: 'cast',
      });
      onToast({ type: 'success', message: `Mapped ${sourceField} → ${targetField} with CAST` });
      clearSelection();
      await queryClient.invalidateQueries(['mappings', batchId]);
      await refetchCanvasGraph();
      setPendingCreateSelection({ targetTable, targetField });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create mapping';
      onToast({ type: 'error', message: msg });
    } finally {
      setIsCreatingMapping(false);
    }
  }, [typeMismatch, activeWorkbookId, queryClient, refetchCanvasGraph, onToast, clearSelection, setPendingCreateSelection]);

  // Phase 4.7: Create mapping without CAST (keep as-is)
  const handleTypeMismatchProceed = useCallback(async () => {
    if (!typeMismatch || !activeWorkbookId) return;
    const { sourceTable, sourceField, targetTable, targetField } = typeMismatch;
    setTypeMismatch(null);
    setIsCreatingMapping(true);
    try {
      await apiService.createMapping(activeWorkbookId, {
        target_table: targetTable,
        target_field: targetField,
        source_file: sourceTable || undefined,
        source_field: sourceField || undefined,
        transformation: sourceField ? `${sourceTable}.${sourceField}` : '',
        transformation_type: 'direct',
      });
      onToast({ type: 'success', message: `Mapped ${sourceField} → ${targetField}` });
      clearSelection();
      await queryClient.invalidateQueries(['mappings', batchId]);
      await refetchCanvasGraph();
      setPendingCreateSelection({ targetTable, targetField });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create mapping';
      onToast({ type: 'error', message: msg });
    } finally {
      setIsCreatingMapping(false);
    }
  }, [typeMismatch, activeWorkbookId, queryClient, refetchCanvasGraph, onToast, clearSelection, setPendingCreateSelection]);

  // --- Regenerate transformation ---
  const handleRegenerateTransformation = useCallback(
    async (mapping: FieldMapping, context: string) => {
      if (!activeWorkbookId) {
        onToast({ type: 'error', message: 'No active workbook found' });
        return;
      }
      const edge = canvasGraph.edges.find(
        (e) => e.data?.mappingId === mapping.mapping_id,
      );
      if (!edge) {
        onToast({ type: 'error', message: 'Could not find edge for this mapping' });
        return;
      }
      setIsRegeneratingMapping(true);
      try {
        await apiService.regenerateCanvasEdge(activeWorkbookId, edge.id, {
          actor: 'user',
          instructions: `Regenerate transformation for ${context}. Improve the logic based on source and target types.`,
        });
        onToast({ type: 'success', message: 'Transformation regenerated successfully' });
        await queryClient.invalidateQueries(['mappings', batchId]);
        void refetchCanvasGraph();
      } catch (err) {
        console.error('Failed to regenerate transformation:', err);
        const msg =
          err instanceof Error ? err.message : 'Failed to regenerate transformation';
        onToast({ type: 'error', message: msg });
      } finally {
        setIsRegeneratingMapping(false);
      }
    },
    [activeWorkbookId, canvasGraph.edges, queryClient, refetchCanvasGraph, onToast],
  );

  // --- Validate transformation ---
  const handleValidateTransformation = useCallback(async (expression: string) => {
    setIsValidatingMapping(true);
    try {
      if (!expression || expression.trim() === '') {
        onToast({ type: 'error', message: 'Expression is empty' });
        return;
      }
      const result = await apiService.validateExpression(expression, []);
      if (result.valid) {
        const typeHint = result.analysis?.output_type_hint;
        onToast({
          type: 'success',
          message: typeHint ? `Expression is valid (output: ${typeHint})` : 'Expression is valid',
        });
      } else {
        onToast({ type: 'error', message: `Validation failed: ${result.error || 'Syntax error'}` });
      }
    } catch (err) {
      onToast({ type: 'error', message: `Validation failed: ${toErrorMessage(err, 'Unknown error')}` });
    } finally {
      setIsValidatingMapping(false);
    }
  }, [onToast]);

  // --- Save handler ---
  const handleSave = useCallback(
    (
      mapping: NonNullable<MappingResponse['mappings']>[number],
      values: TransformationFormValues,
      activeStep: TransformationStep | undefined,
    ) => {
      if (guardrailStatus.blocked) {
        const message =
          guardrailStatus.message ??
          'Align project and schema guardrails before saving changes.';
        onInspectorStatus({ type: 'error', message });
        onInspectorError(message);
        onToast({ type: 'error', message });
        recordCanvasEvent('canvas.guardrail.blocked_save', {
          batch_id: batchId ?? '',
          workbook_id: activeWorkbookId ?? '',
          mapping_id: mapping.mapping_id,
          project_id: guardrailStatus.graphProjectId ?? '',
          schema_id: guardrailStatus.graphSchemaId ?? '',
        });
        return;
      }
      if (!mappingResponse?.recipe_id) {
        const message = 'Recipe context missing; unable to save.';
        onInspectorStatus({ type: 'error', message });
        onInspectorError(message);
        onToast({ type: 'error', message });
        return;
      }

      const payload: UpdateMappingRequest = {
        recipe_id: mappingResponse.recipe_id,
        edited_by: 'web-ui',
      };

      // Re-pointing: include source/target binding changes
      if (values.sourceFile !== undefined && values.sourceFile !== mapping.source_file) {
        payload.source_file = values.sourceFile;
      }
      if (values.sourceField !== undefined && values.sourceField !== mapping.source_field) {
        payload.source_field = values.sourceField;
      }
      if (values.targetTable !== undefined && values.targetTable !== mapping.target_table) {
        payload.target_table = values.targetTable;
      }
      if (values.targetField !== undefined && values.targetField !== mapping.target_field) {
        payload.target_field = values.targetField;
      }

      const trimmedExplanation = values.explanation.trim();
      if (trimmedExplanation !== (mapping.explanation ?? '')) {
        payload.explanation = trimmedExplanation;
      }
      if (values.defaultValue !== stringifyDefaultValue(mapping.default_value)) {
        payload.default_value = parseDefaultValue(values.defaultValue);
      }
      if (values.isRequired !== Boolean(mapping.is_required)) {
        payload.is_required = values.isRequired;
      }
      if (
        typeof values.confidence === 'number' &&
        values.confidence !== mapping.confidence
      ) {
        payload.confidence = Math.min(1, Math.max(0, values.confidence));
      }

      if (values.removeTransformation) {
        payload.remove_transformation = true;
        payload.transformation_type = 'direct';
      } else {
        const trimmedTransformation = values.transformation.trim();
        if (
          trimmedTransformation &&
          trimmedTransformation !== (mapping.transformation ?? '')
        ) {
          payload.transformation = trimmedTransformation;
        }
        const desiredOperation =
          values.operation ||
          mapping.operation ||
          activeStep?.operation ||
          mapping.transformation_type ||
          'expression';
        const desiredType =
          values.operation || mapping.transformation_type || 'expression';
        if (desiredOperation !== (mapping.operation || '')) {
          payload.operation = desiredOperation;
        }
        if (desiredType !== mapping.transformation_type) {
          payload.transformation_type = desiredType;
        }

        const inputlessOps = new Set(['set_constant', 'set_null', 'constant', 'literal', 'generate_uuid']);
        const inputs = inputlessOps.has(desiredOperation)
          ? []
          : activeStep?.inputs?.length
            ? activeStep.inputs
            : [`${mapping.source_file}.${mapping.source_field}`];
        const outputs = activeStep?.outputs?.length
          ? activeStep.outputs
          : [`${mapping.target_table}.${mapping.target_field}`];
        const stepId =
          activeStep?.step_id ??
          mapping.transformation_steps?.[0]?.step_id ??
          mapping.mapping_id;
        payload.step = {
          step_id: stepId,
          operation: desiredOperation,
          description: values.stepDescription.trim(),
          inputs,
          outputs,
          confidence:
            typeof values.confidence === 'number'
              ? values.confidence
              : mapping.confidence,
          ai_generated: values.aiGenerated,
        };
        if (trimmedTransformation || (values.parameters && Object.keys(values.parameters).length > 0)) {
          payload.step.parameters = {
            ...(values.parameters || {}),
            ...(trimmedTransformation ? { expression: trimmedTransformation } : {}),
          };
        }
      }

      const targetId = `edge:${mapping.mapping_id}`;
      pendingManualAdjustmentRef.current = {
        target_id: targetId,
        target_kind: 'edge',
        author: 'web-ui',
        rationale:
          values.explanation.trim() ||
          `Updated mapping ${mapping.source_file}.${mapping.source_field} → ${mapping.target_table}.${mapping.target_field}`,
        status: 'pending_review',
        ai_generated: values.aiGenerated,
        reversible: true,
      };

      updateMappingMutation.mutate({
        mappingId: mapping.mapping_id,
        payload,
        targetField: mapping.target_field,
      });
    },
    [
      mappingResponse,
      updateMappingMutation,
      guardrailStatus,
      recordCanvasEvent,
      batchId,
      activeWorkbookId,
      onInspectorStatus,
      onInspectorError,
      onToast,
    ],
  );

  // --- Apply rule ---
  const handleApplyRule = useCallback(
    (
      mapping: NonNullable<MappingResponse['mappings']>[number],
      step: TransformationStep,
      rule: TransformationRule,
    ) => {
      if (guardrailStatus.blocked) {
        const message =
          guardrailStatus.message ??
          'Align project and schema guardrails before applying rules.';
        onInspectorStatus({ type: 'error', message });
        onInspectorError(message);
        onToast({ type: 'error', message });
        recordCanvasEvent('canvas.guardrail.blocked_rule', {
          batch_id: batchId ?? '',
          workbook_id: activeWorkbookId ?? '',
          mapping_id: mapping.mapping_id,
          rule_id: rule.rule_id,
          project_id: guardrailStatus.graphProjectId ?? '',
          schema_id: guardrailStatus.graphSchemaId ?? '',
        });
        return;
      }
      if (!mappingResponse?.recipe_id) {
        const message = 'Recipe context missing; unable to apply rule.';
        onInspectorStatus({ type: 'error', message });
        onInspectorError(message);
        onToast({ type: 'error', message });
        return;
      }

      const payload: ApplyTransformationRuleRequest = {
        recipe_id: mappingResponse.recipe_id,
        mapping_id: mapping.mapping_id,
        rule_id: rule.rule_id,
        step_id: step.step_id,
        target_table: mapping.target_table,
        target_field: mapping.target_field,
        inputs: step.inputs,
        outputs: step.outputs,
      };

      const targetId = `edge:${mapping.mapping_id}`;
      pendingManualAdjustmentRef.current = {
        target_id: targetId,
        target_kind: 'edge',
        author: 'web-ui',
        rationale: rule.description
          ? `Applied rule ${rule.name}: ${rule.description}`
          : `Applied rule ${rule.name}`,
        status: 'pending_review',
        ai_generated: false,
        reversible: true,
      };
      applyRuleMutation.mutate(payload);
    },
    [
      applyRuleMutation,
      mappingResponse,
      guardrailStatus,
      recordCanvasEvent,
      batchId,
      activeWorkbookId,
      onInspectorStatus,
      onInspectorError,
      onToast,
    ],
  );

  // --- Generate draft ---
  const handleGenerateDraft = useCallback(() => {
    if (guardrailStatus.blocked) {
      const message =
        guardrailStatus.message ??
        'Align project and schema guardrails before regenerating mappings.';
      onToast({ type: 'error', message });
      recordCanvasEvent('canvas.guardrail.blocked_generate', {
        batch_id: batchId ?? '',
        workbook_id: activeWorkbookId ?? '',
        project_id: guardrailStatus.graphProjectId ?? '',
        schema_id: guardrailStatus.graphSchemaId ?? '',
      });
      return;
    }
    if (!selectedProjectId) {
      onToast({
        type: 'error',
        message: 'Select a project before regenerating mappings.',
      });
      return;
    }
    generateMappingsMutation.mutate();
  }, [
    guardrailStatus,
    onToast,
    recordCanvasEvent,
    batchId,
    activeWorkbookId,
    selectedProjectId,
    generateMappingsMutation,
  ]);

  // --- Approve mapping ---
  const handleApproveMapping = useCallback(() => {
    if (guardrailStatus.blocked) {
      const message =
        guardrailStatus.message ??
        'Align project and schema guardrails before approving mappings.';
      onToast({ type: 'error', message });
      recordCanvasEvent('canvas.guardrail.blocked_approve', {
        batch_id: batchId ?? '',
        workbook_id: activeWorkbookId ?? '',
        project_id: guardrailStatus.graphProjectId ?? '',
        schema_id: guardrailStatus.graphSchemaId ?? '',
      });
      return;
    }
    approveMappingsMutation.mutate();
  }, [
    guardrailStatus,
    onToast,
    recordCanvasEvent,
    batchId,
    activeWorkbookId,
    approveMappingsMutation,
  ]);

  // --- Schema enrichment ---
  const handleSaveSchemaEnrichment = useCallback(
    async ({
      targetKind,
      targetId,
      enrichment,
      annotations,
    }: {
      targetKind: 'schema_table' | 'schema_field';
      targetId: string;
      enrichment: Record<string, unknown>;
      annotations?: Record<string, unknown>;
    }) => {
      if (guardrailStatus.blocked) {
        const message =
          guardrailStatus.message ??
          'Align project and schema guardrails before editing enrichment.';
        onInspectorError(message);
        onInspectorStatus({ type: 'error', message });
        onToast({ type: 'error', message });
        recordCanvasEvent('canvas.guardrail.blocked_schema_enrichment', {
          batch_id: batchId ?? '',
          workbook_id: activeWorkbookId ?? '',
          project_id: guardrailStatus.graphProjectId ?? '',
          schema_id: guardrailStatus.graphSchemaId ?? '',
        });
        throw new Error(message);
      }
      if (!activeWorkbookId) throw new Error('Canvas workbook unavailable.');

      const adjustmentAnnotations: Record<string, unknown> = { source: 'canvas-inspector' };
      if (annotations) {
        Object.entries(annotations).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            adjustmentAnnotations[key] = value;
          }
        });
      }

      try {
        onInspectorError(null);
        await apiService.createCanvasManualAdjustment(activeWorkbookId, {
          target_id: targetId,
          target_kind: targetKind,
          author: 'web-ui',
          rationale:
            targetKind === 'schema_table'
              ? `Updated table enrichment via inspector (${String(adjustmentAnnotations.target_table ?? targetId)})`
              : `Updated field enrichment via inspector (${String(adjustmentAnnotations.target_field ?? targetId)})`,
          status: 'pending_review',
          annotations: adjustmentAnnotations,
          details: { enrichment },
        });
        await refetchCanvasGraph();
        onInspectorStatus({ type: 'success', message: 'Schema enrichment saved.' });
        onToast({ type: 'success', message: 'Schema enrichment saved.' });
        recordCanvasEvent('canvas.schema_enrichment.updated', {
          target_kind: targetKind,
          target_id: targetId,
        });
      } catch (error) {
        const message = toErrorMessage(error, 'Failed to save schema enrichment.');
        onInspectorStatus({ type: 'error', message });
        onInspectorError(message);
        onToast({ type: 'error', message });
        throw error;
      }
    },
    [
      activeWorkbookId,
      recordCanvasEvent,
      refetchCanvasGraph,
      guardrailStatus,
      onInspectorError,
      onInspectorStatus,
      onToast,
      batchId,
    ],
  );

  // --- Feedback success ---
  const handleFeedbackSuccess = useCallback(async () => {
    if (batchId) {
      await queryClient.invalidateQueries(['mappings', batchId]);
    }
    if (activeWorkbookId) {
      await queryClient.invalidateQueries(['canvas-graph', activeWorkbookId]);
    }
    await refetchCanvasGraph();
  }, [batchId, activeWorkbookId, queryClient, refetchCanvasGraph]);

  // --- Refresh ---
  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries(['mappings', batchId]);
    if (activeWorkbookId) {
      void refetchCanvasGraph();
    }
    recordCanvasEvent('canvas.graph.refreshed', {
      batch_id: batchId ?? '',
      workbook_id: activeWorkbookId ?? '',
    });
  }, [activeWorkbookId, batchId, queryClient, recordCanvasEvent, refetchCanvasGraph]);

  return {
    // Mutations
    generateMappingsMutation,
    approveMappingsMutation,
    updateMappingMutation,
    deleteMappingMutation,
    applyRuleMutation,
    undoManualAdjustmentMutation,
    redoManualAdjustmentMutation,

    // State
    deleteMappingDialog,
    setDeleteMappingDialog,
    undoingAdjustmentId,
    redoingAdjustmentId,
    isRegeneratingMapping,
    isValidatingMapping,
    isCreatingMapping,

    // Handlers
    handleDeleteMapping,
    handleConfirmDeleteMapping,
    handleCreateMapping,
    handleCanvasConnect,
    handleRegenerateTransformation,
    handleValidateTransformation,
    handleSave,
    handleApplyRule,
    handleGenerateDraft,
    handleApproveMapping,
    handleSaveSchemaEnrichment,
    handleUndoAdjustment,
    handleRedoAdjustment,
    handleFeedbackSuccess,
    handleRefresh,
    updateCanvasGraphCache,

    // Phase 4.7: Type mismatch popover
    typeMismatch,
    setTypeMismatch,
    handleTypeMismatchApplyCast,
    handleTypeMismatchProceed,
  };
}
